import React, { useEffect } from 'react'
import { Controller, Form, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Group, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import * as yup from 'yup'

import { getNextClosingStep } from './closingNavigation'
import {
  gateConfirmOrders,
  resolveClosingSyncEffect,
  undoErrataRecipientIfCompleted,
} from './closingOrders'
import { ClosingVerbatim } from './ClosingVerbatim'
import { ErrataRecipientContent } from './ErrataRecipientContent'
import { ReadWaiveNotAskedNote } from './ReadWaiveNotAskedNote'
import {
  TranscriptOrderCollectionContent,
  TranscriptOrderConfirmationContent,
} from './TranscriptOrderActions'

import { ReadWaiveSelect } from '@/components/common/ReadWaiveSelect'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { SpellingsForm } from '@/components/Event/NewScript/components/common/forms/SpellingsForm'
import { TimestampsForm } from '@/components/Event/NewScript/components/common/forms/TimestampsForm'
import { ActionButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionButton'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { ContinueButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ContinueButton'
import { ScriptTimeline } from '@/components/Event/NewScript/components/common/ScriptTimeline/ScriptTimeline'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { ScriptActions } from '@/components/Event/NewScript/hooks/state/useScriptActions'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import { useEndZoomEvent } from '@/components/Event/NewScript/hooks/useEndZoomEvent'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import {
  EVENT_FORM_CONTEXT_CAN_WITNESS_READ_OR_WAIVE,
  EVENT_FORM_CONTEXT_ERRATA_APPLIES,
  EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED,
  EVENT_FORM_CONTEXT_IS_EUO,
  EVENT_FORM_CONTEXT_IS_FINAL_SCRIPT_EVENT,
} from '@/components/Event/NewScript/schemas'
import { closingStepSchema } from '@/components/Event/NewScript/schemas/closingStepSchema'
import {
  doesErrataApply,
  readWaiveAnswerToRecord,
} from '@/components/Event/NewScript/utils/errata'
import {
  getWitnessEventTitle,
  isExaminationUnderOath,
} from '@/components/Event/NewScript/utils/event'
import {
  AttendeeRole,
  DBFContact,
  DepositionResult,
  ScriptStep,
  TimestampType,
} from '@/constants'
import { Event, EventScriptDataObject, ReadWaiveAnswer } from '@/types'
import { getEventTimezone } from '@/utils/time'

export type ClosingStepInterface = yup.InferType<typeof closingStepSchema>

type DefaultClosingFormProps = {
  step: string
  event: Event
  state: EventScriptDataObject
  actions: ScriptActions
  witnessIndex: number | undefined
  scriptEventCount: number
}

export const DefaultClosingForm: React.FC<DefaultClosingFormProps> = ({
  step,
  event,
  state,
  actions,
  witnessIndex,
  scriptEventCount,
}) => {
  const {
    canWitnessReadOrWaive,
    shouldConfirmStipulations,
    nonDefaultClosingVerbatim,
  } = useStatePolicies(event)

  const isEUO = isExaminationUnderOath(event)

  const errataApplies = doesErrataApply(state.read_waive_answer)
  const answerToRecord = readWaiveAnswerToRecord(
    { isAsked: canWitnessReadOrWaive, isEUO },
    state.read_waive_answer,
  )

  const {
    canContinue,
    completeStepAndContinue,
    completeAction,
    isActionCompleted,
    undoAction,
  } = useStepActions(step, event, {
    hiddenSatisfiedActions: [
      ...(shouldConfirmStipulations ? [] : ['confirm_stipulations']),
      ...(errataApplies ? [] : ['select_errata_recipient']),
    ],
  })

  const { confirmAndEndEvent, isLoading } = useEndZoomEvent(event)
  const nextStep = getNextClosingStep({
    eventCount: scriptEventCount,
    witnessIndex,
  })
  const isFinalScriptEvent = nextStep === ScriptStep.REVIEW

  const onContinueClick = () => {
    if (nextStep === ScriptStep.REVIEW) {
      confirmAndEndEvent(() => {
        completeStepAndContinue(nextStep)
      })
    } else {
      completeStepAndContinue(nextStep)
    }
  }

  const methods = useForm({
    mode: 'all',
    criteriaMode: 'all',
    resolver: yupResolver(closingStepSchema),
    context: {
      [EVENT_FORM_CONTEXT_CAN_WITNESS_READ_OR_WAIVE]: canWitnessReadOrWaive,
      [EVENT_FORM_CONTEXT_ERRATA_APPLIES]: errataApplies,
      [EVENT_FORM_CONTEXT_IS_EUO]: isEUO,
      [EVENT_FORM_CONTEXT_IS_DOCUMENT_NUMBER_REQUIRED]: false,
      [EVENT_FORM_CONTEXT_IS_FINAL_SCRIPT_EVENT]: isFinalScriptEvent,
    },
    defaultValues: {
      read_waive_answer: state.read_waive_answer,
      errata_recipient_attendee_id: state.errata_recipient_attendee_id,
      order_transcript: state.order_transcript,
      attendees: state.attendees,
      is_on_record: state.is_on_record ?? false,
      attendees_have_left_call: isActionCompleted('attendees_have_left_call'),
      spellings: state.spellings,
      timestamps: state.timestamps,
    },
  })

  useEffect(() => {
    actions.updateEventResult(DepositionResult.COMPLETED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    methods.setValue('is_on_record', state.is_on_record ?? false, {
      shouldDirty: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.is_on_record])

  useEffect(() => {
    if (answerToRecord !== state.read_waive_answer) {
      actions.updateReadWaiveAnswer(answerToRecord)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerToRecord, state.read_waive_answer])

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      const effect = resolveClosingSyncEffect(name, value)
      if (!effect) {
        return
      }
      switch (effect.type) {
        case 'readWaiveAnswer':
          if (effect.value !== 'read') {
            undoErrataRecipientIfCompleted(isActionCompleted, undoAction)
          }
          return actions.updateReadWaiveAnswer(effect.value)
        case 'errataRecipient':
          return actions.updateErrataRecipient(effect.value)
        case 'orderTranscript':
          return actions.updateOrderTranscript(effect.value)
        case 'attendees':
          return actions.updateAttendees({ attendees: effect.attendees })
        case 'spellings':
          return actions.updateSpellings(effect.value)
        case 'timestamps':
          return actions.updateTimestamps(effect.value)
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, state, actions])

  const validateAndCompleteAction = async (
    validation_keys: Array<keyof ClosingStepInterface>,
    action_key: string,
  ) => {
    const results: Promise<boolean>[] = []

    validation_keys.forEach(key => {
      results.push(methods.trigger(key))
    })

    Promise.all(results).then(res => {
      res.every(isValid => isValid) && completeAction(action_key)
    })
  }
  const completeAttendeesHaveLeftCallAction = async () => {
    methods.setValue('attendees_have_left_call', true, {
      shouldValidate: true,
    })
    const isValid = await methods.trigger('attendees_have_left_call')

    if (isValid) {
      completeAction('attendees_have_left_call')
    }
  }

  const witnessName =
    witnessIndex !== undefined
      ? state.attendees.find(a => a.role === AttendeeRole.WITNESS)?.name
      : undefined

  return (
    <FormProvider {...methods}>
      <Form>
        <Stack align="flex-start" spacing="xl">
          <ScriptTimeline
            title={
              witnessIndex === undefined
                ? '5. Closing'
                : `${witnessIndex + 2}.3 Closing - ${getWitnessEventTitle(event, witnessName)}`
            }
          >
            <TimelineAction
              actionKey="clarify_exhibit"
              title="Clarify exhibit information"
              onComplete={() => {
                validateAndCompleteAction(['timestamps'], 'clarify_exhibit')
              }}
              completed={isActionCompleted('clarify_exhibit')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionContainer>
                    <TimestampsForm />
                  </ActionContainer>
                </Stack>
              </ActionContainer>
            </TimelineAction>
            <TimelineAction
              actionKey="remind_exhibit"
              title="Remind attendees to email exhibits"
              onComplete={() => {
                validateAndCompleteAction([], 'remind_exhibit')
              }}
              completed={isActionCompleted('remind_exhibit')}
            >
              <ActionContainer outer>
                <ActionText mono>
                  I would like to remind the counsels that if any exhibits were
                  presented in this deposition, they should be emailed to
                  exhibits@filevine.com so that we can ensure the exhibits are
                  included in the transcript.
                </ActionText>
              </ActionContainer>
            </TimelineAction>
            <TimelineAction
              actionKey="clarify_spelling"
              title="Clarify spelling information if necessary"
              onComplete={() => {
                validateAndCompleteAction(['spellings'], 'clarify_spelling')
              }}
              completed={isActionCompleted('clarify_spelling')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionContainer>
                    <SpellingsForm />
                  </ActionContainer>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      If the witness or attorney used a name, a location, or a
                      term during questioning that are not common words, ask
                      them to clarify the correct spelling.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>
            </TimelineAction>
            {!isEUO && (
              <TimelineAction
                actionKey="confirm_read_or_waive"
                title="Confirm read or waive"
                onComplete={() => {
                  validateAndCompleteAction(
                    ['read_waive_answer'],
                    'confirm_read_or_waive',
                  )
                }}
                completed={isActionCompleted('confirm_read_or_waive')}
              >
                {!canWitnessReadOrWaive && <ReadWaiveNotAskedNote />}
                {canWitnessReadOrWaive && (
                  <ActionContainer outer>
                    <Stack>
                      <ActionText mono>
                        May I please confirm if the witness will read or waive
                        the transcript of today’s deposition if it is ordered?
                      </ActionText>
                      <ActionContainer>
                        <Stack>
                          <Controller
                            control={methods.control}
                            name={'read_waive_answer'}
                            render={({ field: { onChange, value } }) => (
                              <ReadWaiveSelect
                                value={value}
                                onChange={(newValue: ReadWaiveAnswer) => {
                                  onChange(newValue)
                                  if (newValue !== 'read') {
                                    methods.setValue(
                                      'errata_recipient_attendee_id',
                                      null,
                                    )
                                  }
                                }}
                                label="Read or waive"
                                style={{ width: 200 }}
                                error={
                                  !!methods.formState.errors.read_waive_answer
                                    ?.message
                                }
                              />
                            )}
                          />
                          {methods.formState.errors.read_waive_answer !==
                            undefined && (
                            <FormErrors
                              errors={
                                methods.formState.errors.read_waive_answer
                                  .message
                              }
                            />
                          )}
                        </Stack>
                      </ActionContainer>
                      <ActionContainer>
                        <ActionTitle>Reminder</ActionTitle>
                        <ActionText>
                          The attorney representing witness may decide on the
                          witness’ behalf if they will read or waive.
                        </ActionText>
                      </ActionContainer>
                    </Stack>
                  </ActionContainer>
                )}
              </TimelineAction>
            )}
            {errataApplies && (
              <TimelineAction
                actionKey="select_errata_recipient"
                title="Confirm who receives the errata sheet"
                onComplete={() => {
                  validateAndCompleteAction(
                    ['errata_recipient_attendee_id', 'attendees'],
                    'select_errata_recipient',
                  )
                }}
                completed={isActionCompleted('select_errata_recipient')}
              >
                <ErrataRecipientContent />
              </TimelineAction>
            )}
            {!isEUO && (
              <TimelineAction
                actionKey="collect_transcript_orders"
                title="Collect transcript orders"
                onComplete={() =>
                  validateAndCompleteAction(
                    ['order_transcript', 'attendees'],
                    'collect_transcript_orders',
                  )
                }
                completed={isActionCompleted('collect_transcript_orders')}
              >
                <TranscriptOrderCollectionContent
                  event={event}
                  state={state}
                  isActionCompleted={isActionCompleted}
                  undoAction={undoAction}
                />
              </TimelineAction>
            )}
            {!isEUO && (
              <TimelineAction
                actionKey="confirm_orders"
                title="Confirm transcript orders with attendees"
                onComplete={() => {
                  const gate = gateConfirmOrders(
                    isActionCompleted('collect_transcript_orders'),
                  )
                  if (!gate.allowed) {
                    showNotification(gate.notification)
                    return
                  }
                  validateAndCompleteAction(['attendees'], 'confirm_orders')
                }}
                completed={isActionCompleted('confirm_orders')}
              >
                <TranscriptOrderConfirmationContent state={state} />
              </TimelineAction>
            )}
            {shouldConfirmStipulations && (
              <TimelineAction
                actionKey="confirm_stipulations"
                title="Confirm stipulations"
                onComplete={() => completeAction('confirm_stipulations')}
                completed={isActionCompleted('confirm_stipulations')}
              >
                <ActionContainer outer>
                  <ActionText mono>
                    Are there any stipulations for the record?
                  </ActionText>
                </ActionContainer>
              </TimelineAction>
            )}
            <TimelineAction
              actionKey="go_off_the_record"
              title={
                nonDefaultClosingVerbatim
                  ? 'Confirm any remaining questions or stipulations and go off the record'
                  : 'Go off the record and answer remaining questions'
              }
              onComplete={() =>
                validateAndCompleteAction(['is_on_record'], 'go_off_the_record')
              }
              completed={isActionCompleted('go_off_the_record')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    <ClosingVerbatim event={event} />
                  </ActionText>
                  <ActionContainer>
                    <Group>
                      <ActionButton
                        onClick={() => {
                          actions.addTimestamp({
                            isOnRecord: false,
                            timestampType: TimestampType.RECORDING_OFF,
                            timezone: getEventTimezone(event),
                          })
                          methods.setValue('is_on_record', false)
                          methods.trigger('is_on_record')
                        }}
                        disabled={!state.is_on_record}
                      >
                        Go off record
                      </ActionButton>
                      {!state.is_on_record && (
                        <Text weight={700} size="xs">
                          You are now off the record.
                        </Text>
                      )}
                    </Group>
                  </ActionContainer>
                  {!!methods.formState.errors.is_on_record?.message && (
                    <FormErrors
                      errors={methods.formState.errors.is_on_record?.message}
                    />
                  )}
                </Stack>
              </ActionContainer>
              <ActionContainer outer>
                <ActionContainer>
                  <ActionTitle>Reminder</ActionTitle>
                  <ActionText>
                    Address any outstanding questions or concerns.
                    <br />
                    Exhibits not already provided should be sent to{' '}
                    {DBFContact.EXHIBITS_EMAIL}.
                    <br />
                    Transcript orders, cost breakdowns, and other requests
                    should be sent to {DBFContact.SUPPORT_EMAIL}.
                  </ActionText>
                </ActionContainer>
              </ActionContainer>
            </TimelineAction>
            {isFinalScriptEvent && (
              <TimelineAction
                actionKey="attendees_have_left_call"
                title="Confirm all attendees have left the call"
                onComplete={() => completeAttendeesHaveLeftCallAction()}
                completed={isActionCompleted('attendees_have_left_call')}
                collapsible={false}
              />
            )}
            <ContinueButton
              event={event}
              label={
                nextStep === ScriptStep.REVIEW
                  ? 'Continue to review'
                  : 'Continue to next witness'
              }
              canContinue={canContinue()}
              step={ScriptStep.CLOSING}
              handleClick={() => onContinueClick()}
              loading={isLoading}
              endsZoomCallOnContinue={nextStep === ScriptStep.REVIEW}
              reminder={
                isFinalScriptEvent &&
                !isActionCompleted('attendees_have_left_call')
                  ? '(Please wait to move on until all attendees have left the call.)'
                  : undefined
              }
            />
          </ScriptTimeline>
        </Stack>
      </Form>
    </FormProvider>
  )
}
